/**
 * The Cloud plans — what a cloud tenant pays US to host its own application.
 *
 * Second product of io.ankhos, beside the AnkhOS operator bands in `tiers.ts`.
 *
 * # 🔑 THE PLAN IS CHOSEN, NOT DERIVED
 *
 * Unlike the operator bands, a cloud tenant picks its plan (Build while
 * developing, Launch at production go-live). There is no count to measure, so
 * this file has no `bandFor` — and the pricing page may show these as options.
 *
 * # 🔑 PRICES ARE PLUS GST (owner 2026-10-25, template v2)
 *
 * "for new boarding, we can charge 18% more ie 1800 + 18% GST." `price` is
 * the plan price excl. GST, exactly what the invoice line carries; GST at 18%
 * is added on the invoice. (v1, the same morning, was GST-inclusive and never
 * took effect. Arogyakundali keeps its own ₹1,800-all-in plan, not on this
 * ladder.)
 *
 * # ⚠️ THIS IS A MIRROR, AND MIRRORS DRIFT
 *
 * Authority: `io.ankhos/templates/cloud-plans-v2.md` (template_version 2),
 * compiled into `radbilling_component` rows `AnkhOS Cloud …`. Same reason as
 * `tiers.ts` for keeping a copy here: the public page has no route to fetch
 * them from.
 *
 * Allowances, overage, the 3× ceiling and the two-month step-up are PUBLISHED
 * terms with no meter behind them yet; the page says what they are, never that
 * anything enforces them.
 */

export const CLOUD_SOURCE = {
  repo: "io.ankhos",
  file: "templates/cloud-plans-v2.md",
  templateId: "cloud-plans",
  templateVersion: 2,
  approvedOn: "2026-10-25",
  effectiveFrom: "2026-11-01",
} as const;

export const CLOUD_GST_PERCENT = 18;

/** 1 CU = 1 vCPU with 2 GB memory for one hour, charged on whichever binds. */
export const CU_HOURS_ALWAYS_ON = 730;

export interface CloudPlan {
  tier: "build" | "launch" | "grow" | "scale";
  label: string;
  /** The plan price a month, excl. GST -- what the invoice line carries. */
  price: number;
  cu: number;
  blockStorageGb: number;
  objectStorageGb: number;
  transferOutGb: number;
  /** null = by agreement. */
  ceilingCu: number | null;
  /** excl. GST */
  maxInvoice: number;
  support: string;
  workload: string;
}

export const CLOUD_PLANS: readonly CloudPlan[] = [
  {
    tier: "build", label: "Build", price: 1000,
    cu: 150, blockStorageGb: 10, objectStorageGb: 20, transferOutGb: 5,
    ceilingCu: 550, maxInvoice: 3000,
    support: "Email, 2 business days",
    workload: "Development and staging that sleeps outside working hours",
  },
  {
    tier: "launch", label: "Launch", price: 1800,
    cu: 450, blockStorageGb: 30, objectStorageGb: 100, transferOutGb: 20,
    ceilingCu: 1170, maxInvoice: 5400,
    support: "Email and phone, next business day",
    workload: "Production, always-on",
  },
  {
    tier: "grow", label: "Grow", price: 5000,
    cu: 1400, blockStorageGb: 100, objectStorageGb: 400, transferOutGb: 75,
    ceilingCu: 3400, maxInvoice: 15000,
    support: "24×7 response",
    workload: "Production, multi-customer",
  },
  {
    tier: "scale", label: "Scale", price: 14000,
    cu: 4500, blockStorageGb: 300, objectStorageGb: 1536, transferOutGb: 250,
    ceilingCu: null, maxInvoice: 42000,
    support: "24×7 with a named contact",
    workload: "Production, dedicated capacity",
  },
];

/** Every cloud bill is capped at this multiple of the plan price. */
export const CLOUD_BILL_CEILING_MULTIPLE = 3;

/** Published overage, excl. GST. Not billable yet: no meter. */
export const CLOUD_OVERAGE = {
  gracePercent: 10,
  cuBlockSize: 100,
  cuBlock: 350,
  cuAfterBlocks: 5,
  blockStorageGb: 15,
  objectHotGb: 4,
  objectArchiveGb: 1.5,
  transferOutGb: 12,
} as const;

/** Quoted per tenant, not plans. Excl. GST. */
export const CLOUD_EXTRAS: readonly { name: string; price: string }[] = [
  { name: "Managed PostgreSQL / MySQL", price: "₹500 per instance + compute at ₹3 per CU" },
  { name: "High availability", price: "₹1,000 per instance + compute × 3 replicas" },
  { name: "Managed cache (Redis / Valkey)", price: "₹300 per instance + compute" },
  { name: "Committed compute, 12 months", price: "₹3 per CU" },
];

export const CLOUD_INCLUDED: readonly string[] = [
  "Managed Kubernetes hosting, tenants isolated at network, identity and data layers",
  "Identity and fine-grained authorisation for your application",
  "Automated daily backups",
  "Usage dashboard against your allowance, and notice at 80%, 100% and 120%",
  "Monthly GST invoice in INR",
];

/** Storage in GB, shown as TB from 1,024 up. */
export function formatGb(gb: number): string {
  return gb >= 1024 ? `${(gb / 1024).toLocaleString("en-IN", { maximumFractionDigits: 1 })} TB` : `${gb} GB`;
}

/** The invoice total for a plan price: same state (CGST + SGST) or IGST. */
export function invoiceTotal(price: number, sameState: boolean): number {
  const r2 = (n: number) => Math.round(n * 100 + 1e-6) / 100;
  const tax = sameState ? 2 * r2((price * CLOUD_GST_PERCENT) / 200) : r2((price * CLOUD_GST_PERCENT) / 100);
  return r2(price + tax);
}
